/**
 * Post carrier informations
 *
 **/
import { Injectable } from '@angular/core';
import { IMargin, IPrice, IPriceDetails } from 'libraries/service/resources/booking/interfaces/IGetBookings';
import { EXRateType, ITotal } from "libraries/interfaces/ITotal";
import { IPriceDetail } from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import _cloneDeep from "lodash-es/cloneDeep";

@Injectable()
export class PriceService {

    private $post = JSON.parse(sessionStorage.getItem('$post'));

    public getPrice(_searchRQ: any): number {
        return _searchRQ.price;
    }

    public getTotalPrice(totals: ITotal[]): { cost: number; retail: number; } {
        const totalRetail: ITotal = totals.find((total: ITotal) =>
            total.xType === EXRateType.retail
        );
        const totalCost: ITotal = totals.find((total: ITotal) =>
            total.xType === EXRateType.cost
        );

        return {
            cost: totalCost ? totalCost.value : null,
            retail: totalRetail ? totalRetail.value : null,
        };
    }


    public getTotalFromPrices(prices: IPrice[], hasRetailPrice: boolean): ITotal[] {
        const costValue: number = prices
            .filter((price: IPrice) => price.xType === EXRateType.cost && price.xTarget === 'total')
            .reduce(reduceValue, 0);
        const currency: string = prices
            .filter((price: IPrice) => price.xType === EXRateType.cost && price.xTarget === 'total')
            [0]
            .total.base.currency;
        const costTotal: ITotal = {
            value: costValue,
            amount: costValue / 100,
            currency,
            decimals: 2,
            xType: EXRateType.cost
        };
        if (!hasRetailPrice) {
            return [costTotal];
        }
        const retailValue: number = prices
            .filter((price: IPrice) => price.xType === EXRateType.retail && price.xTarget === 'total')
            .reduce(reduceValue, 0);
        return [
            costTotal,
            {
                value: retailValue,
                amount: retailValue / 100,
                currency,
                decimals: 2,
                xType: EXRateType.retail
            }
        ];
    }


    public getRetailTitle(prices: IPrice[]): string {
        const retail: IPrice[] = prices
            .filter((priceItem: IPrice) => priceItem.xType === EXRateType.retail && priceItem.title);
        const title: string = retail.length > 0 ? (retail[0].title || null) : null;
        return title;
    }


    public getComputedRetailTransportationPrice(bestQuotePrice: IPrice[], initialQuotePrice: IPrice[], bookingTotal?: ITotal[]): IPrice[] {
        const initialQuoteTransportationRetailPrice: IPrice = initialQuotePrice
            .find((price: IPrice): boolean => price.xTarget === "total" && price.xRole === "transportation" && price.xType === "retail");
        const calculationMode: 'margin' | 'markup' | null = initialQuoteTransportationRetailPrice.margin ?
            'margin' : initialQuoteTransportationRetailPrice.markup ?
                'markup' : null;

        if (!initialQuoteTransportationRetailPrice || !calculationMode) {
            return null;
        }
        const computedRetailTransportationPrice: IPrice[] = [...bestQuotePrice]
            .filter((price: IPrice) => price.xTarget === "total" && price.xType === "cost")
            .map((price: IPrice) => {
                price[calculationMode] = initialQuoteTransportationRetailPrice[calculationMode];
                price.detail = this.getComputedRetailTransportationPriceDetail(price.detail, price[calculationMode], calculationMode);
                let totalAmount: number = 0;
                Object.values(price.detail).forEach((item: IPriceDetail): void => {
                    totalAmount += item[0].amount;
                });
                // @ts-ignore
                price.total.total.amount = totalAmount;
                // @ts-ignore
                price.total.total.value = totalAmount*100;
                price.xType = EXRateType.retail;
                return price;
            });
        return computedRetailTransportationPrice;
    }


    private getComputedRetailTransportationPriceDetail(
        priceDetails: IPriceDetails,
        retailCalculationRule: IMargin,
        calculationMode: 'margin' | 'markup'): IPriceDetails {
        const priceDetailKeys: string[] = Object.keys(priceDetails);
        const computedPriceDetails: IPriceDetails = priceDetailKeys.reduce((acc: IPriceDetails, priceDetailKey: string): IPriceDetails => {
            if (!acc[priceDetailKey]) {
                acc[priceDetailKey] = [];
            }
            const xUnitValue: number = this.calculateRetailPrice(
                priceDetails[priceDetailKey][0].unitValue,
                retailCalculationRule[priceDetailKey].reference,
                calculationMode
            );
            const value: number = xUnitValue * priceDetails[priceDetailKey][0].quantity;
            acc[priceDetailKey].push({
                ...priceDetails[priceDetailKey][0],
                xUnitValue,
                xUnitAmount: xUnitValue / 100,
                unitValue: xUnitValue,
                unitAmount: xUnitValue / 100,
                value,
                amount: value / 100
            });
            return acc;
        }, {base: []});
        return computedPriceDetails;
    }

    private calculateRetailPrice(costValue: number, calculationReference: number, calculationMode: 'margin' | 'markup'): number {
        return calculationMode === 'margin' ?
            Math.round(costValue / (1 - calculationReference)) :
            calculationMode === 'markup' ?
                Math.round(costValue * calculationReference + 1) :
                null;
    }
}

function reduceValue(acc: number, {detail, ...price}: IPrice): number {
    const priceTotal: number = Object.keys(detail)
        .map((detailKey: string) => detail[detailKey][0].value)
        .reduce((numAcc: number, value: number) => (numAcc + value), 0);
    return acc + priceTotal;
}
